// frontend/src/components/Estimates/EstimateCategoryTabs.jsx

/**
 * Вкладка «Сметы» с разбивкой на 3 подвкладки:
 * - Работы (операции)
 * - Оборудование
 * - Материалы
 *
 * На каждой вкладке - общая таблица, агрегированная из всех смет проекта,
 * с возможностью вручную добавить позицию (сохраняется в "ручную" смету
 * карты на бэкенде и попадает в отчёт).
 */

import { useMemo, useState } from 'react'
import { Card } from '../Common/Card'
import { EstimateCalculationPanel, ReplaceItemModal } from './EstimateList'
import {
  Package, Wrench, Zap, Plus, X, Loader, MapPin, AlertTriangle,
  Eye, EyeOff, FileText, Hand, RefreshCw, Database
} from 'lucide-react'
import toast from 'react-hot-toast'
import { estimatesAPI, gesnAPI } from '../../services/api'

const itemCost = (item) => item?.total_cost ?? item?.total_price ?? 0

const getRankColor = (rank) => {
  if (!rank || rank === 0) return 'bg-red-100 text-red-700'
  if (rank >= 0.8) return 'bg-green-100 text-green-700'
  if (rank >= 0.5) return 'bg-yellow-100 text-yellow-700'
  if (rank >= 0.3) return 'bg-orange-100 text-orange-700'
  return 'bg-red-100 text-red-700'
}

const CATEGORY_CONFIG = {
  works: {
    label: 'Работы',
    icon: Zap,
    color: 'purple',
    itemType: 'operations',
    addLabel: 'Добавить работу'
  },
  equipment: {
    label: 'Оборудование',
    icon: Wrench,
    color: 'orange',
    itemType: 'equipment',
    addLabel: 'Добавить оборудование'
  },
  materials: {
    label: 'Материалы',
    icon: Package,
    color: 'blue',
    itemType: 'materials',
    addLabel: 'Добавить материал'
  }
}

// ============ МОДАЛКА РУЧНОГО ДОБАВЛЕНИЯ ПОЗИЦИИ ============

const AddItemModal = ({ isOpen, onClose, category, cardId, onAdded }) => {
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const config = CATEGORY_CONFIG[category]
  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }))

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      toast.error('Введите название позиции')
      return
    }

    setSaving(true)
    try {
      const payload = {
        card_id: cardId,
        item_type: config.itemType,
        name: form.name.trim(),
        code: form.code || null,
        unit: form.unit || null,
        // materials
        quantity: form.quantity ? Number(form.quantity) : null,
        unit_price: form.unit_price ? Number(form.unit_price) : null,
        gost_part: form.gost_part || null,
        gost_material: form.gost_material || null,
        // equipment
        hours: form.hours ? Number(form.hours) : null,
        price_per_hour: form.price_per_hour ? Number(form.price_per_hour) : null,
        category: form.category || null,
        // works
        time: form.time ? Number(form.time) : null,
        operation_type: form.operation_type || null,
        description: form.description || null
      }

      await estimatesAPI.addManualItem(payload)
      toast.success(`Позиция «${form.name.trim()}» добавлена в смету`)
      setForm({})
      onAdded?.()
      onClose()
    } catch (error) {
      console.error('Add manual item error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при добавлении позиции')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white rounded-t-xl">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Plus size={18} className="text-blue-600" />
            {config.addLabel}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* Название - общее для всех */}
          <div>
            <label className={labelCls}>Название <span className="text-red-500">*</span></label>
            <input
              type="text" value={form.name || ''} onChange={set('name')}
              placeholder={category === 'materials' ? 'Болт М10х50' : category === 'equipment' ? 'Станок сверлильный' : 'Сверление отверстий'}
              className={inputCls} autoFocus
            />
          </div>

          {category === 'materials' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Код</label>
                  <input type="text" value={form.code || ''} onChange={set('code')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Ед. изм.</label>
                  <input type="text" value={form.unit || ''} onChange={set('unit')} placeholder="шт" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>ГОСТ детали</label>
                  <input type="text" value={form.gost_part || ''} onChange={set('gost_part')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>ГОСТ материала</label>
                  <input type="text" value={form.gost_material || ''} onChange={set('gost_material')} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Количество</label>
                  <input type="number" min="0" step="any" value={form.quantity || ''} onChange={set('quantity')} placeholder="1" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Цена за ед., ₽</label>
                  <input type="number" min="0" step="any" value={form.unit_price || ''} onChange={set('unit_price')} placeholder="0" className={inputCls} />
                </div>
              </div>
            </>
          )}

          {category === 'equipment' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Код</label>
                  <input type="text" value={form.code || ''} onChange={set('code')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Категория</label>
                  <input type="text" value={form.category || ''} onChange={set('category')} placeholder="станки" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Часов</label>
                  <input type="number" min="0" step="any" value={form.hours || ''} onChange={set('hours')} placeholder="1" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Цена за час, ₽</label>
                  <input type="number" min="0" step="any" value={form.price_per_hour || ''} onChange={set('price_per_hour')} placeholder="0" className={inputCls} />
                </div>
              </div>
            </>
          )}

          {category === 'works' && (
            <>
              <div>
                <label className={labelCls}>Тип работы</label>
                <input type="text" value={form.operation_type || ''} onChange={set('operation_type')} placeholder="Сверление, сварка..." className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Описание</label>
                <textarea value={form.description || ''} onChange={set('description')} rows={2} className={inputCls} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Время</label>
                  <input type="number" min="0" step="any" value={form.time || ''} onChange={set('time')} placeholder="1" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Ед. изм.</label>
                  <input type="text" value={form.unit || ''} onChange={set('unit')} placeholder="ч" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Цена за ед., ₽</label>
                  <input type="number" min="0" step="any" value={form.unit_price || ''} onChange={set('unit_price')} placeholder="0" className={inputCls} />
                </div>
              </div>
            </>
          )}

          {/* Предпросмотр суммы */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 flex justify-between">
            <span>Сумма позиции:</span>
            <span className="font-bold text-green-600">
              {(() => {
                const qty = Number(
                  category === 'materials' ? (form.quantity || 1)
                  : category === 'equipment' ? (form.hours || 1)
                  : (form.time || 1)
                )
                const price = Number(
                  category === 'equipment' ? (form.price_per_hour || 0) : (form.unit_price || 0)
                )
                return (qty * price).toLocaleString('ru-RU')
              })()} ₽
            </span>
          </div>
        </div>

        {/* Кнопки */}
        <div className="flex gap-2 justify-end px-5 py-4 border-t sticky bottom-0 bg-white rounded-b-xl">
          <button
            onClick={onClose} disabled={saving}
            className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={handleSubmit} disabled={saving || !form.name?.trim()}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
            Добавить
          </button>
        </div>
      </div>
    </div>
  )
}

// ============ ОБЩИЕ ЭЛЕМЕНТЫ ТАБЛИЦ ============

const GesnStatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-400 text-xs">—</span>
  const map = {
    auto: { cls: 'bg-green-100 text-green-700', label: 'Авто' },
    reviewed: { cls: 'bg-emerald-100 text-emerald-800', label: 'Проверено' },
    needs_review: { cls: 'bg-yellow-100 text-yellow-800', label: 'На проверку' },
    price_required: { cls: 'bg-orange-100 text-orange-800', label: 'Нет цены' },
  }
  const item = map[status] || { cls: 'bg-gray-100 text-gray-600', label: status }
  return <span className={`px-2 py-0.5 text-xs rounded ${item.cls}`}>{item.label}</span>
}

const SourceBadge = ({ row }) => (
  row._manual ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded">
      <Hand size={10} />
      Ручная
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
      <FileText size={10} />
      {row._source}
    </span>
  )
)

// Источник цены (площадка справочника): Минстрой, Ручной ввод и т.д.
const PriceSourceBadge = ({ row }) => {
  const src = row.source || (row._manual ? 'Ручной ввод' : null)
  if (!src) {
    return <span className="text-gray-400 text-xs">-</span>
  }
  const isManual = src === 'Ручной ввод'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${
      isManual ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'
    }`}>
      <Database size={10} />
      {src}
    </span>
  )
}

const NameCell = ({ row }) => (
  <div className="flex flex-col">
    <span className={`font-medium text-gray-900 ${row.excluded ? 'line-through text-gray-400' : ''}`}>
      {row.name}
      {row.excluded && (
        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">исключено</span>
      )}
    </span>
    {row.warning && !row.excluded && (
      <span className="text-xs text-red-600 flex items-center gap-1 mt-0.5">
        <AlertTriangle size={11} />
        {row.warning}
      </span>
    )}
  </div>
)

const ExcludeButton = ({ row, busyKey, onToggle }) => {
  if (row._isFixed) return null
  const key = `${row._estimateId}-${row._index}`
  const busy = busyKey === key
  return (
    <button
      onClick={() => onToggle(row)}
      disabled={busy}
      title={row.excluded ? 'Включить обратно' : 'Исключить из сметы'}
      className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${
        row.excluded
          ? 'text-green-600 hover:bg-green-50'
          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      }`}
    >
      {busy ? <Loader size={15} className="animate-spin" /> : row.excluded ? <Eye size={15} /> : <EyeOff size={15} />}
    </button>
  )
}

// Кнопка "Заменить из справочника" (материалы и оборудование)
const ReplaceButton = ({ row, onOpenReplace }) => {
  if (row._isFixed || row.excluded) return null
  return (
    <button
      onClick={() => onOpenReplace(row)}
      title="Заменить из справочника"
      className="p-1.5 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
    >
      <RefreshCw size={15} />
    </button>
  )
}

// ============ ГЛАВНЫЙ КОМПОНЕНТ ============

export const EstimateCategoryTabs = ({ estimates, sheets, cardId, onRefresh }) => {
  const [activeCategory, setActiveCategory] = useState('works')
  const [addModal, setAddModal] = useState(false)
  const [busyKey, setBusyKey] = useState(null)
  // Модалка замены позиции из справочника: { row } | null
  const [replaceTarget, setReplaceTarget] = useState(null)
  const [gesnReview, setGesnReview] = useState(null)

  // Смета, которой принадлежит заменяемая позиция (нужна ReplaceItemModal)
  const replaceEstimate = useMemo(() => {
    if (!replaceTarget) return null
    return (estimates || []).find(e => e.id === replaceTarget._estimateId) || null
  }, [replaceTarget, estimates])

  // Мапа sheet_id -> page_number для указания источника
  const pageBySheet = useMemo(() => {
    const map = {}
    for (const s of sheets || []) map[s.id] = s.page_number
    return map
  }, [sheets])

  const sourceLabel = (est) =>
    est.sheet_id ? `Лист ${pageBySheet[est.sheet_id] ?? est.sheet_id}` : 'Ручная'

  // Агрегация позиций из всех смет по категориям
  const aggregated = useMemo(() => {
    const works = []
    const equipment = []
    const materials = []

    for (const est of estimates || []) {
      const meta = (list, itemType) => (item, index) => ({
        ...item,
        _estimateId: est.id,
        _itemType: itemType,
        _index: index,
        _isFixed: !!est.is_fixed,
        _manual: !!item.manual || (!est.sheet_id && est.estimate_type !== 'gesn'),
        _source: est.estimate_type === 'gesn'
          ? (item.justification || 'ГЭСН')
          : sourceLabel(est)
      })

      if (est.estimate_type === 'materials') {
        const items = est.data?.materials?.length ? est.data.materials : (est.data?.items || [])
        materials.push(...items.map(meta(items, 'materials')))
      } else if (est.estimate_type === 'equipment') {
        const items = est.data?.equipment?.length ? est.data.equipment : (est.data?.items || [])
        equipment.push(...items.map(meta(items, 'equipment')))
        // Материалы внутри сборочных смет (если есть)
        if (est.data?.materials?.length) {
          materials.push(...est.data.materials.map(meta(est.data.materials, 'materials')))
        }
      } else if (est.estimate_type === 'operations' || est.estimate_type === 'gesn') {
        const ops = est.data?.operations || est.data?.items || []
        works.push(...ops.map(meta(ops, 'operations')))
        const eq = est.data?.equipment || []
        equipment.push(...eq.map(meta(eq, 'equipment')))
        if (est.data?.materials?.length) {
          materials.push(...est.data.materials.map(meta(est.data.materials, 'materials')))
        }
      }
    }

    return { works, equipment, materials }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimates, pageBySheet])

  const gesnCoverage = useMemo(() => {
    const gesn = (estimates || []).find((e) => e.estimate_type === 'gesn')
    return gesn?.data?.coverage || null
  }, [estimates])

  // Исключение/включение позиции
  const handleToggleExclusion = async (row) => {
    const key = `${row._estimateId}-${row._index}`
    setBusyKey(key)
    try {
      await estimatesAPI.toggleItemExclusion(row._estimateId, {
        item_type: row._itemType,
        item_index: row._index,
        excluded: !row.excluded
      })
      toast.success(!row.excluded ? 'Позиция исключена из сметы' : 'Позиция включена обратно')
      onRefresh?.()
    } catch (error) {
      console.error('Toggle exclusion error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при изменении позиции')
    } finally {
      setBusyKey(null)
    }
  }

  const activeRows = aggregated[activeCategory] || []
  const activeTotal = activeRows.filter(r => !r.excluded).reduce((s, r) => s + itemCost(r), 0)
  const config = CATEGORY_CONFIG[activeCategory]

  const colorMap = {
    purple: { active: 'border-purple-600 text-purple-600', badge: 'bg-purple-100 text-purple-700' },
    orange: { active: 'border-orange-600 text-orange-600', badge: 'bg-orange-100 text-orange-700' },
    blue: { active: 'border-blue-600 text-blue-600', badge: 'bg-blue-100 text-blue-700' }
  }

  return (
    <div className="space-y-4">
      {/* Панель расчёта смет по региону */}
      {cardId && (
        <EstimateCalculationPanel
          cardId={cardId}
          hasEstimates={(estimates || []).length > 0}
          onCalculated={() => onRefresh?.()}
        />
      )}

      {/* Подвкладки: Работы / Оборудование / Материалы */}
      <div className="flex gap-1 border-b">
        {Object.entries(CATEGORY_CONFIG).map(([key, cat]) => {
          const Icon = cat.icon
          const count = (aggregated[key] || []).length
          const isActive = activeCategory === key
          return (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                isActive
                  ? colorMap[cat.color].active
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              {cat.label}
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                isActive ? colorMap[cat.color].badge : 'bg-gray-100 text-gray-500'
              }`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {gesnCoverage && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="px-2 py-1 bg-emerald-50 text-emerald-800 rounded">
            ЛСР ГЭСН: {gesnCoverage.lines || 0} строк
          </span>
          <span className="px-2 py-1 bg-green-50 text-green-700 rounded">авто {gesnCoverage.auto || 0}</span>
          <span className="px-2 py-1 bg-yellow-50 text-yellow-800 rounded">на проверку {gesnCoverage.needs_review || 0}</span>
          <span className="px-2 py-1 bg-orange-50 text-orange-800 rounded">нет цены {gesnCoverage.price_required || 0}</span>
        </div>
      )}

      {/* Таблица активной категории */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <config.icon size={18} />
            {config.label} — общая таблица
          </h3>
          <button
            onClick={() => setAddModal(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-1.5"
          >
            <Plus size={15} />
            {config.addLabel}
          </button>
        </div>

        {activeRows.length === 0 ? (
          <div className="text-center py-10">
            <config.icon size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 mb-1">Позиции отсутствуют</p>
            <p className="text-sm text-gray-500">
              Рассчитайте сметы по региону или добавьте позицию вручную.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* ======== РАБОТЫ ======== */}
            {activeCategory === 'works' && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-3 font-semibold">Работа</th>
                    <th className="text-center py-3 px-3 font-semibold">Обоснование</th>
                    <th className="text-center py-3 px-3 font-semibold">Статус</th>
                    <th className="text-center py-3 px-3 font-semibold">Тип</th>
                    <th className="text-center py-3 px-3 font-semibold">Источник</th>
                    <th className="text-center py-3 px-3 font-semibold">Кол-во</th>
                    <th className="text-center py-3 px-3 font-semibold">Изм.</th>
                    <th className="text-right py-3 px-3 font-semibold">Сумма</th>
                    <th className="text-right py-3 px-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeRows.map((row, i) => (
                    <tr key={i} className={`border-b transition-colors ${row.excluded ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                      <td className="py-3 px-3">
                        <NameCell row={row} />
                        {row.rationale && row.status && row.status !== 'auto' && (
                          <span className="text-xs text-gray-500">{row.rationale}</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-3">
                        {row.justification ? (
                          <span className="font-mono text-xs bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded">
                            {row.justification}
                          </span>
                        ) : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      <td className="text-center py-3 px-3">
                        <GesnStatusBadge status={row.status} />
                      </td>
                      <td className="text-center py-3 px-3">
                        {row.operation_type ? (
                          <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs">{row.operation_type}</span>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                      <td className="text-center py-3 px-3"><SourceBadge row={row} /></td>
                      <td className="text-center py-3 px-3">{row.quantity ?? row.time ?? '-'}</td>
                      <td className="text-center py-3 px-3 text-gray-600">{row.unit || (row.time != null ? 'ч' : '-')}</td>
                      <td className={`text-right py-3 px-3 font-semibold ${row.excluded ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                        {itemCost(row) ? `${itemCost(row).toLocaleString('ru-RU')} ₽` : <span className="text-gray-400 font-normal">-</span>}
                      </td>
                      <td className="text-right py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          {row.candidates?.length > 0 && !row._isFixed && (
                            <button
                              onClick={() => setGesnReview(row)}
                              title="Выбрать расценку ГЭСН"
                              className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
                            >
                              <RefreshCw size={15} />
                            </button>
                          )}
                          <ExcludeButton row={row} busyKey={busyKey} onToggle={handleToggleExclusion} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ======== ОБОРУДОВАНИЕ ======== */}
            {activeCategory === 'equipment' && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-3 font-semibold">Оборудование</th>
                    <th className="text-center py-3 px-3 font-semibold">Источник</th>
                    <th className="text-center py-3 px-3 font-semibold">Ист. цены</th>
                    <th className="text-center py-3 px-3 font-semibold">Регион</th>
                    <th className="text-center py-3 px-3 font-semibold">Точность</th>
                    <th className="text-center py-3 px-3 font-semibold">Часов</th>
                    <th className="text-right py-3 px-3 font-semibold">Цена/час</th>
                    <th className="text-right py-3 px-3 font-semibold">Сумма</th>
                    <th className="text-right py-3 px-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeRows.map((row, i) => (
                    <tr key={i} className={`border-b transition-colors ${row.excluded ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                      <td className="py-3 px-3"><NameCell row={row} /></td>
                      <td className="text-center py-3 px-3"><SourceBadge row={row} /></td>
                      <td className="text-center py-3 px-3"><PriceSourceBadge row={row} /></td>
                      <td className="text-center py-3 px-3">
                        {row.region ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                            <MapPin size={10} />{row.region}
                          </span>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                      <td className="text-center py-3 px-3">
                        {row.rank !== undefined && row.rank !== null ? (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getRankColor(row.rank)}`}>
                            {(row.rank * 100).toFixed(0)}%
                          </span>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                      <td className="text-center py-3 px-3">{row.hours ?? '-'}</td>
                      <td className="text-right py-3 px-3">
                        {(row.price_per_hour ?? 0).toLocaleString('ru-RU')} ₽
                      </td>
                      <td className={`text-right py-3 px-3 font-semibold ${row.excluded ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                        {itemCost(row).toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="text-right py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          {!row._manual && (
                            <ReplaceButton row={row} onOpenReplace={setReplaceTarget} />
                          )}
                          <ExcludeButton row={row} busyKey={busyKey} onToggle={handleToggleExclusion} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ======== МАТЕРИАЛЫ ======== */}
            {activeCategory === 'materials' && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-3 font-semibold">Материал</th>
                    <th className="text-center py-3 px-3 font-semibold">Источник</th>
                    <th className="text-center py-3 px-3 font-semibold">Ист. цены</th>
                    <th className="text-center py-3 px-3 font-semibold">Регион</th>
                    <th className="text-center py-3 px-3 font-semibold">Точность</th>
                    <th className="text-center py-3 px-3 font-semibold">Кол-во</th>
                    <th className="text-center py-3 px-3 font-semibold">Ед.</th>
                    <th className="text-right py-3 px-3 font-semibold">Цена/ед.</th>
                    <th className="text-right py-3 px-3 font-semibold">Сумма</th>
                    <th className="text-right py-3 px-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeRows.map((row, i) => (
                    <tr key={i} className={`border-b transition-colors ${row.excluded ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                      <td className="py-3 px-3">
                        <NameCell row={row} />
                        {(row.gost_part || row.gost_material) && (
                          <span className="text-xs text-gray-500">
                            {[row.gost_part, row.gost_material].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </td>
                      <td className="text-center py-3 px-3"><SourceBadge row={row} /></td>
                      <td className="text-center py-3 px-3"><PriceSourceBadge row={row} /></td>
                      <td className="text-center py-3 px-3">
                        {row.region ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                            <MapPin size={10} />{row.region}
                          </span>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                      <td className="text-center py-3 px-3">
                        {row.rank !== undefined && row.rank !== null ? (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getRankColor(row.rank)}`}>
                            {(row.rank * 100).toFixed(0)}%
                          </span>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                      <td className="text-center py-3 px-3">{row.quantity ?? '-'}</td>
                      <td className="text-center py-3 px-3 text-gray-600">{row.unit || '-'}</td>
                      <td className="text-right py-3 px-3">
                        {(row.unit_price ?? 0).toLocaleString('ru-RU')} ₽
                      </td>
                      <td className={`text-right py-3 px-3 font-semibold ${row.excluded ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                        {itemCost(row).toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="text-right py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          {!row._manual && (
                            <ReplaceButton row={row} onOpenReplace={setReplaceTarget} />
                          )}
                          <ExcludeButton row={row} busyKey={busyKey} onToggle={handleToggleExclusion} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Итог по категории */}
        {activeRows.length > 0 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <span className="text-sm text-gray-600">
              Позиций: {activeRows.length}
              {activeRows.some(r => r.excluded) && (
                <span className="text-gray-400"> ({activeRows.filter(r => r.excluded).length} искл.)</span>
              )}
            </span>
            <span className="text-lg font-bold text-green-600">
              Итого: {activeTotal.toLocaleString('ru-RU')} ₽
            </span>
          </div>
        )}
      </Card>

      {/* Модалка добавления позиции */}
      <AddItemModal
        isOpen={addModal}
        onClose={() => setAddModal(false)}
        category={activeCategory}
        cardId={cardId}
        onAdded={() => onRefresh?.()}
      />

      {/* Модалка замены позиции из справочника */}
      <ReplaceItemModal
        isOpen={!!replaceTarget && !!replaceEstimate}
        onClose={() => setReplaceTarget(null)}
        estimate={replaceEstimate}
        itemType={replaceTarget?._itemType}
        itemIndex={replaceTarget?._index}
        currentItem={replaceTarget}
        onReplaced={() => {
          setReplaceTarget(null)
          onRefresh?.()
        }}
      />

      <GesnReviewModal
        row={gesnReview}
        onClose={() => setGesnReview(null)}
        onApplied={() => {
          setGesnReview(null)
          onRefresh?.()
        }}
      />
    </div>
  )
}

const GesnReviewModal = ({ row, onClose, onApplied }) => {
  const [saving, setSaving] = useState(false)
  if (!row) return null
  const candidates = row.candidates || []

  const apply = async (code) => {
    setSaving(true)
    try {
      await gesnAPI.review(row._estimateId, { line_no: row.no, norm_code: code })
      toast.success(`Принята расценка ${code}`)
      onApplied?.()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось применить расценку')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h3 className="font-semibold">Выбор расценки ГЭСН</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-700">{row.description || row.name}</p>
          {row.rationale && <p className="text-xs text-gray-500">{row.rationale}</p>}
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500">Нет кандидатов</p>
          ) : candidates.map((c) => (
            <div key={c.code} className="border rounded-lg p-3 flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-xs text-emerald-800">{c.code}</div>
                <div className="text-sm text-gray-900">{c.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Измеритель: {c.unit} · сборник {c.collection}
                </div>
              </div>
              <button
                disabled={saving}
                onClick={() => apply(c.code)}
                className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                Выбрать
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default EstimateCategoryTabs
